import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation, useHistory } from 'react-router-dom';

import { VaultDocument } from '@/types/vault';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import NoRowsOverlay from '@/@mui/data-grid/NoRowsOverlay';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useRenameDocument } from '@/api/endpoints/document/useRenameDocument';
import { useUpdateCategoryOfDocument } from '@/api/endpoints/document/useUpdateCategoryOfDocument';
import { useDeleteVaultDocument } from '@/api/endpoints/vault/useDeleteVaultDocument';
import { useGetDocumentCategories } from '@/api/endpoints/vault/useGetDocumentCategories';
import { useGetVaultDocuments } from '@/api/endpoints/vault/useGetVaultDocuments';
import { useUploadVaultDocument } from '@/api/endpoints/vault/useUploadVaultDocument';
import { EditIcon } from '@/assets/icons/EditIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { useAuth } from '@/context/AuthProvider';
import { DocumentStatusProvider } from '@/context/DocumentStatusProvider';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatFullDate } from '@/utils/date';

import RowActionsMenu from '@/components/common/RowActionsMenu';
import DocumentCategoryCell from '@/components/document/DocumentCategoryCell';
import DocumentDeleteModal from '@/components/document/DocumentDeleteModal';
import DocumentUploadButton, {
  API as DocumentUploadButtonAPI,
} from '@/components/document/DocumentUploadButton';
import DocumentViewModal from '@/components/document/DocumentViewModal';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';
import VaultDocumentTagModal from '@/components/vault/VaultDocumentTagModal';
import VaultDocumentTags from '@/components/vault/VaultDocumentTags';

interface FilterFormValues {
  categoryId: string;
  searchTerm: string;
}

interface DropZoneProps {
  onFileDrop: (file: File) => void;
  isUploading: boolean;
  children: React.ReactNode;
}

const DropZone: React.FC<DropZoneProps> = ({ onFileDrop, isUploading, children }) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);

      if (isUploading) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        onFileDrop(file);
      }
    },
    [onFileDrop, isUploading]
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        '&::after': isDragging
          ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            zIndex: 10,
            pointerEvents: 'none',
          }
          : {},
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 11,
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <UploadCloud01Icon sx={{ fontSize: 48, color: 'primary.main' }} />
          <Typography variant="h6" color="primary.main" sx={{ mt: 2 }}>
            Drop file to upload
          </Typography>
        </Box>
      )}
    </Box>
  );
};

interface DocumentNameEditorProps {
  initialValue: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
}

const DocumentNameEditor: React.FC<DocumentNameEditorProps> = ({
  initialValue,
  onSave,
  onCancel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Focus the input when the component mounts
    if (inputRef.current) {
      inputRef.current.focus();
      // Place cursor at the end
      inputRef.current.selectionStart = inputRef.current.value.length;
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Handle Enter to save
    if (event.key === 'Enter') {
      if (inputRef.current && inputRef.current.value.trim()) {
        onSave(inputRef.current.value.trim());
      } else {
        onCancel();
      }
    } else if (event.key === 'Escape') {
      onCancel();
    }
    event.stopPropagation();
  };

  const handleBlur = () => {
    if (inputRef.current && inputRef.current.value.trim()) {
      onSave(inputRef.current.value.trim());
    } else {
      onCancel();
    }
  };

  // Prevent any click events from bubbling up to parent elements
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <input
      ref={inputRef}
      defaultValue={initialValue}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      onClick={handleClick}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #1976d2',
        borderRadius: '4px',
        padding: '0 8px',
        fontSize: 'inherit',
        outline: 'none',
      }}
    />
  );
};

const VaultPage: React.FC = () => {
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const { setOnboardingProgress } = useOnboardingProgress();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const history = useHistory();
  const pendingCategoryIdRef = useRef<number | null>(null);
  const dialogOpenedRef = useRef<boolean>(false);

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<VaultDocument | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<VaultDocument | null>(null);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentCategoryId, setPreviewDocumentCategoryId] = useState<number | null>(null);
  const documentUploadButtonRef = useRef<DocumentUploadButtonAPI>(null);

  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(null);

  // Setup react-hook-form for filters
  const filterForm = useForm<FilterFormValues>({
    defaultValues: {
      categoryId: '',
      searchTerm: '',
    },
  });

  const { watch, reset, control } = filterForm;
  const { categoryId, searchTerm } = watch();

  // Fetch document categories
  const { data: categories = [] } = useGetDocumentCategories();

  const {
    data: documents = [],
    isLoading,
    refetch,
  } = useGetVaultDocuments(
    {
      property_location_id: selectedPropertyLocation?.id,
    },
    !!session?.access_token
  );

  // Filter documents by category ID and search term if provided
  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Filter by category if selected
    if (categoryId) {
      filtered = filtered.filter(doc => doc.category_id === categoryId);
    }

    // Filter by search term if provided
    if (searchTerm) {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = filtered.filter(doc => {
        // Search in document name
        if (doc.original_name.toLowerCase().includes(searchTermLower)) {
          return true;
        }

        // Search in category name
        const category = categories.find(c => c.id === doc.category_id);
        if (category && category.name.toLowerCase().includes(searchTermLower)) {
          return true;
        }

        // Search in property location names
        if (doc.property_locations && Array.isArray(doc.property_locations)) {
          for (const loc of doc.property_locations) {
            if (loc.name && loc.name.toLowerCase().includes(searchTermLower)) {
              return true;
            }
          }
        }

        // Search in building names and addresses
        if (doc.buildings && Array.isArray(doc.buildings)) {
          for (const building of doc.buildings) {
            if (
              (building.name && building.name.toLowerCase().includes(searchTermLower)) ||
              (building.address_text &&
                building.address_text.toLowerCase().includes(searchTermLower))
            ) {
              return true;
            }
          }
        }

        // Search in unit names
        if (doc.units && Array.isArray(doc.units)) {
          for (const unit of doc.units) {
            if (unit.name && unit.name.toLowerCase().includes(searchTermLower)) {
              return true;
            }
          }
        }

        // Search in tenant names
        if (doc.tenants && Array.isArray(doc.tenants)) {
          for (const tenant of doc.tenants) {
            if (tenant.full_name && tenant.full_name.toLowerCase().includes(searchTermLower)) {
              return true;
            }
          }
        }

        // Search in property manager names
        if (doc.property_managers && Array.isArray(doc.property_managers)) {
          for (const manager of doc.property_managers) {
            if (
              manager.limitedUser &&
              manager.limitedUser.full_name &&
              manager.limitedUser.full_name.toLowerCase().includes(searchTermLower)
            ) {
              return true;
            }
          }
        }

        return false;
      });
    }

    return filtered;
  }, [documents, categoryId, searchTerm, categories]);

  // Reset filters when property location changes
  useEffect(() => {
    if (selectedPropertyLocation) {
      reset({
        categoryId: '',
        searchTerm: '',
      });
    }
  }, [selectedPropertyLocation, reset]);

  const deleteDocumentMutation = useDeleteVaultDocument({
    onSuccess: () => {
      refetch();
    },
  });

  const updateCategoryOfDocumentMutation = useUpdateCategoryOfDocument({
    onSuccess: document => {
      refetch();

      if (document.category_id === 2) {
        setOnboardingProgress({
          hasLeaseDocument: true,
        });
      }
    },
  });

  const uploadDocumentMutation = useUploadVaultDocument({
    onSuccess: document => {
      if (pendingCategoryIdRef.current) {
        updateCategoryOfDocumentMutation.mutate({
          categoryId: pendingCategoryIdRef.current,
          id: document.id,
        });

        pendingCategoryIdRef.current = null;
      }

      // Refetch documents after successful upload
      refetch();
    },
  });

  const renameDocumentMutation = useRenameDocument({
    onSuccess: () => {
      refetch();
    },
    onError: () => {
      enqueueSnackbar('Failed to rename document', { variant: 'error' });
    },
  });

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  const handleUploadClick = useCallback(() => {
    documentUploadButtonRef.current?.click();
  }, []);

  const handleFileSelect = useCallback(
    (file: File) => {
      const searchParams = new URLSearchParams(location.search);
      const defaultCategoryId = searchParams.get('defaultCategoryId');

      // Store category ID for later use after successful upload
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
        enqueueSnackbar('Document will be categorized as a lease agreement', { variant: 'info' });
      }

      uploadDocumentMutation.mutate({
        file,
        tags: selectedPropertyLocation?.id
          ? {
            property_location_ids: [selectedPropertyLocation.id],
            building_ids: [],
            unit_ids: [],
            tenant_ids: [],
            property_manager_ids: [],
          }
          : undefined,
      });
    },
    [uploadDocumentMutation, selectedPropertyLocation, location.search]
  );

  // Check for openDocumentUpload query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openDocumentUpload') === 'true' && !dialogOpenedRef.current) {
      // Mark that we've opened the dialog for this session
      dialogOpenedRef.current = true;

      // Store defaultCategoryId if present
      const defaultCategoryId = searchParams.get('defaultCategoryId');
      if (defaultCategoryId) {
        pendingCategoryIdRef.current = Number(defaultCategoryId);
      }

      // Clear URL parameters immediately
      const newUrl = new URL(window.location.href);
      newUrl.search = '';
      history.replace(newUrl.pathname);

      // Open the upload dialog
      handleUploadClick();
    }
  }, [location.search, handleUploadClick, history]);

  const handleTagDocument = useCallback((document: VaultDocument) => {
    setSelectedDocument(document);
    setIsTagModalOpen(true);
  }, []);

  const handleTagModalClose = useCallback(() => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
  }, []);

  const handleTagModalSuccess = useCallback(() => {
    setIsTagModalOpen(false);
    setSelectedDocument(null);
    refetch();
  }, [refetch]);

  const handlePreviewDocument = useCallback((document: VaultDocument) => {
    setPreviewDocumentId(document.id);
    setPreviewDocumentCategoryId(document.category_id ? Number(document.category_id) : null);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocumentId(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDocumentToDelete(null);
  }, []);

  const handleDocumentDeleted = useCallback(() => {
    if (documentToDelete) {
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);
    }
  }, [documentToDelete]);

  const handleDeleteClick = useCallback((document: VaultDocument) => {
    setDocumentToDelete(document);
    setIsDeleteModalOpen(true);
  }, []);

  const handleStartEditing = useCallback((document: VaultDocument) => {
    setEditingDocumentId(document.id);
  }, []);

  const handleSaveDocumentName = useCallback(
    (newName: string) => {
      if (editingDocumentId && newName) {
        renameDocumentMutation.mutate({
          id: editingDocumentId,
          name: newName,
        });
        setEditingDocumentId(null);
      }
    },
    [editingDocumentId, renameDocumentMutation]
  );

  const handleCancelEditing = useCallback(() => {
    setEditingDocumentId(null);
  }, []);

  const VaultNoRowsOverlay = useCallback(
    () => (
      <NoRowsOverlay
        entityName="Document"
        addEntityButtonIcon={<UploadCloud01Icon />}
        addEntityButtonText="Upload Document"
        isLoading={uploadDocumentMutation.isPending}
        onAddEntityClick={handleUploadClick}
      />
    ),
    [handleUploadClick, uploadDocumentMutation.isPending]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'original_name',
        headerName: 'Document Name',
        flex: isMobile ? 2 : 1,
        minWidth: 100,
        maxWidth: isMobile ? 200 : 300,
        renderCell(params: GridRenderCellParams<VaultDocument>) {
          if (editingDocumentId === params.row.id) {
            return (
              <Box sx={{ width: '100%', p: 0 }} onClick={e => e.stopPropagation()}>
                <DocumentNameEditor
                  initialValue={params.row.original_name}
                  onSave={handleSaveDocumentName}
                  onCancel={handleCancelEditing}
                />
              </Box>
            );
          }

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: isMobile ? '0.8125rem' : 'inherit',
                  py: isMobile ? 0.5 : 0,
                }}
                onClick={() => handlePreviewDocument(params.row)}
              >
                {params.value}
              </Typography>
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  handleStartEditing(params.row);
                }}
                sx={{
                  ml: 0.5,
                  p: 0.5,
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
      {
        field: 'category',
        headerName: 'Document Category',
        width: isMobile ? 130 : 200,
        hide: isMobile && isTablet,
        renderCell: (params: GridRenderCellParams<VaultDocument>) => (
          <DocumentCategoryCell document={params.row} />
        ),
      },
      {
        field: 'tags',
        headerName: 'Tags',
        minWidth: 100,
        flex: 0.8,
        align: 'center',
        headerAlign: 'center',
        hide: isMobile,
        renderCell: (params: GridRenderCellParams<VaultDocument>) => (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <VaultDocumentTags document={params.row} />
          </Box>
        ),
      },
      {
        field: 'created_at',
        headerName: 'Date Added',
        width: isMobile ? 100 : 180,
        hide: isMobile && !isTablet,
        renderCell: (params: GridRenderCellParams<VaultDocument>) => (
          <Typography
            variant="body2"
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: isMobile ? '0.8125rem' : 'inherit',
            }}
          >
            {formatFullDate(new Date(params.value as string))}
          </Typography>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: isMobile ? 80 : 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<VaultDocument>) => (
          <RowActionsMenu
            items={[
              {
                label: 'Edit Tags',
                onClick: () => handleTagDocument(params.row),
                isLoading: false,
              },
              {
                label: 'Delete',
                onClick: () => handleDeleteClick(params.row),
                isLoading:
                  deleteDocumentMutation.isPending && documentToDelete?.id === params.row.id,
              },
            ]}
          />
        ),
      },
    ],
    [
      handleTagDocument,
      handlePreviewDocument,
      handleDeleteClick,
      deleteDocumentMutation.isPending,
      documentToDelete,
      isMobile,
      isTablet,
      editingDocumentId,
      handleStartEditing,
      handleSaveDocumentName,
      handleCancelEditing,
    ]
  );

  const clearFilters = useCallback(() => {
    reset({
      categoryId: '',
      searchTerm: '',
    });
  }, [reset]);

  const hasActiveFilters = useMemo(() => !!categoryId || !!searchTerm, [categoryId, searchTerm]);

  return (
    <MuiPageWrapper>
      <DocumentStatusProvider>
        <FormProvider {...filterForm}>
          <Stack direction="column" spacing={2}>
            <DashboardHeader hasInnerHtml title="Documents: <em>Active</em>" />

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              mb={2}
            >
              <Typography
                variant="dataGridBody"
                sx={{
                  fontSize: { xs: '0.875rem', sm: 'inherit' },
                  mb: { xs: 1, sm: 0 },
                }}
              >
                View and manage your active documents.
              </Typography>
              <Button
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={handleUploadClick}
                disabled={uploadDocumentMutation.isPending}
                fullWidth={isMobile}
                size={isMobile ? 'medium' : 'large'}
                sx={{
                  alignSelf: isMobile ? 'stretch' : 'auto',
                  py: isMobile ? 1 : 1.5,
                  borderRadius: 1.5,
                }}
              >
                Upload Document
              </Button>
            </Stack>

            {/* Filter Controls */}
            <Box
              sx={{
                backgroundColor: 'background.paper',
                py: 2.5,
                px: { xs: 2.5, sm: 3 },
                borderRadius: 2,
                boxShadow: t => ({
                  xs: 'none',
                  sm: t.shadows[1],
                }),
                border: t => `1px solid ${t.palette.divider}`,
                mb: 3,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Filters
              </Typography>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 2, md: 3 }}
                alignItems={{ xs: 'stretch', md: 'flex-end' }}
              >
                <Box sx={{ minWidth: { xs: '100%', sm: 220 } }}>
                  <FormSelect
                    name="categoryId"
                    control={control}
                    label="Document Category"
                    placeholder="All Categories"
                    options={categories.map(category => ({
                      label: category.name,
                      value: category.id,
                    }))}
                    size={isMobile ? 'small' : 'medium'}
                    muiSelectProps={{
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'divider',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}
                  />
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: 300 } }}>
                  <FormTextField
                    control={control}
                    name="searchTerm"
                    label="Search Documents"
                    placeholder="Search by name, category or tags"
                    size={isMobile ? 'small' : 'medium'}
                    textFieldProps={{
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchMdIcon />
                          </InputAdornment>
                        ),
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main',
                          },
                        },
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    mt: { xs: 1, md: 0 },
                  }}
                >
                  {hasActiveFilters ? (
                    <Button
                      variant="outlined"
                      color="error"
                      size={isMobile ? 'small' : 'medium'}
                      onClick={clearFilters}
                      startIcon={<SearchMdIcon />}
                      sx={{
                        minWidth: { xs: '100%', md: 150 },
                        borderRadius: 1.5,
                      }}
                    >
                      Clear Filters
                    </Button>
                  ) : null}
                </Box>
              </Stack>
            </Box>

            <DocumentUploadButton
              ref={documentUploadButtonRef}
              isFileUploading={uploadDocumentMutation.isPending}
              onFileSelect={handleFileSelect}
              sx={{ display: 'none' }}
            />

            <Box
              sx={{
                height: { xs: 600, sm: 700, md: 800 },
                width: '100%',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: { xs: 0, sm: 1 },
                border: { xs: 1, sm: 0 },
                borderColor: 'divider',
              }}
            >
              <DropZone
                onFileDrop={handleFileSelect}
                isUploading={uploadDocumentMutation.isPending}
              >
                <DataGrid
                  rows={filteredDocuments}
                  columns={columns}
                  density={isMobile ? 'compact' : 'comfortable'}
                  disableColumnFilter
                  disableColumnMenu
                  disableEval
                  loading={isLoading}
                  slots={{
                    noRowsOverlay: VaultNoRowsOverlay,
                  }}
                  slotProps={{
                    loadingOverlay: {
                      variant: 'skeleton',
                      noRowsVariant: 'skeleton',
                    },
                  }}
                  sortingMode="server"
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  disableRowSelectionOnClick
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  rowHeight={isMobile ? 52 : 56}
                  sx={{
                    height: '100%',
                    border: 'none',
                    '& .MuiDataGrid-main': {
                      width: '100%',
                    },
                    '& .MuiDataGrid-cell': {
                      display: 'flex',
                      alignItems: 'center',
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 1, sm: 2 },
                      fontSize: { xs: '0.875rem', sm: 'inherit' },
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      opacity: 1,
                      touchAction: 'manipulation',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      px: { xs: 1, sm: 2 },
                      py: { xs: 1, sm: 1.5 },
                      backgroundColor: 'background.paper',
                      borderBottom: 1,
                      borderColor: 'divider',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.875rem', sm: 'inherit' },
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-virtualScroller': {
                      overflowX: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '4px',
                      },
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.2s ease',
                      '&:nth-of-type(even)': {
                        backgroundColor: 'action.hover',
                      },
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                      '&:active': {
                        backgroundColor: 'action.disabledBackground',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: 1,
                      borderColor: 'divider',
                      justifyContent: 'flex-end',
                      px: { xs: 1, sm: 2 },
                    },
                    '& .MuiTablePagination-root': {
                      fontSize: { xs: '0.75rem', sm: 'inherit' },
                    },
                  }}
                />
              </DropZone>
            </Box>

            {isTagModalOpen && selectedDocument && (
              <VaultDocumentTagModal
                document={selectedDocument}
                onClose={handleTagModalClose}
                onSuccess={handleTagModalSuccess}
              />
            )}

            {isDeleteModalOpen && documentToDelete && (
              <DocumentDeleteModal
                document={documentToDelete}
                onClose={handleDeleteModalClose}
                onDocumentDeleted={handleDocumentDeleted}
              />
            )}

            {previewDocumentId && (
              <DocumentViewModal
                id={previewDocumentId}
                categoryId={previewDocumentCategoryId ?? undefined}
                onClose={handleClosePreview}
              />
            )}
          </Stack>
        </FormProvider>
      </DocumentStatusProvider>
    </MuiPageWrapper>
  );
};

export default VaultPage;
